import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum, IFile, SVGFC } from '@repo/toolkit/types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button, { ButtonProps, ButtonVariant } from '@repo/ui/button'
import { DisplayImage, getAcceptAttribute, getFileType } from '@repo/ui/file-input'
import {
  CheckMarkIcon,
  ExclamationMarkOutlinedIcon,
  InvoiceIcon,
  PenIcon,
  UploadIcon,
  XIcon
} from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useEffect, useRef, useState } from 'react'

import {
  CostMissingInvoiceKeys,
  CostMissingInvoices
} from '../../../../api/centers/send-center-final-costs-to-lec'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { FileType } from '../../../../lib/types/models/file/file'
import { LocaleKeys } from '../../../../locale/translation-types'
import { CenterCostsTableMode, CenterCostType } from './utils'

type CenterCostsTableHeaderProps = {
  title: string
  icon?: SVGFC
  mode: CenterCostsTableMode

  disabled?: boolean
  customButton?: ButtonProps
  onSave?: () => Promise<void>
  onCancel?: () => void
  onEditableChange?: (editable: boolean) => void

  validateOptions?: {
    validate: boolean
    locked: boolean
    onLock: () => Promise<void>
    onUnlock: () => Promise<void>
  }
}
export const CenterCostsTableHeader = ({
  icon: Icon,
  title,
  disabled,
  customButton,
  mode,
  validateOptions,
  onEditableChange,
  onCancel,
  onSave
}: CenterCostsTableHeaderProps) => {
  const { t } = useTranslate()
  const [loading, setLoading] = useState(false)
  const [lockLoading, setLockLoading] = useState(false)
  const [editable, setEditable] = useState(mode === 'free-edit' ? true : false)
  const [locked, setLocked] = useState(validateOptions?.locked ?? false)

  const handleSave = async () => {
    setLoading(true)
    await onSave?.()
    setLoading(false)
    setEditable(false)
  }

  const handleLock = async () => {
    setLockLoading(true)
    if (validateOptions?.locked) await validateOptions.onUnlock?.()
    else await validateOptions?.onLock?.()
    setLockLoading(false)
    setLocked((prev) => !prev)
  }

  useEffect(() => {
    onEditableChange?.(editable)
  }, [editable])

  useEffect(() => {
    if (mode === 'free-edit') setEditable(true)
    else setEditable(false)
  }, [mode])

  return (
    <div className={'flex flex-row gap-2 items-center mb-3 w-full'}>
      <div className={'flex flex-row gap-2 items-center w-full'}>
        {Icon && (
          <div className="w-10 h-10 bg-primary-light rounded p-1 fill-primary flex items-center justify-center">
            <Icon color={tailwindColors.primary.DEFAULT} />
          </div>
        )}
        <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
          {title}
        </Typography>
      </div>

      {customButton && <Button {...customButton} />}

      {mode !== 'readonly' && validateOptions?.validate && !editable && !locked && (
        <ActionButton
          variant={'success'}
          icon={CheckMarkIcon}
          disabled={lockLoading}
          onClick={handleLock}
        />
      )}

      {mode !== 'readonly' && validateOptions?.validate && !editable && locked && (
        <ActionButton
          variant={'lightGreen'}
          icon={CheckMarkIcon}
          disabled={lockLoading}
          className={'rounded-full border-none'}
          onClick={handleLock}
        />
      )}

      {mode === 'action-edit' && !editable && !locked && (
        <ActionButton
          variant={'primary'}
          icon={PenIcon}
          disabled={disabled}
          onClick={() => setEditable(true)}
        />
      )}

      {mode === 'action-edit' && editable && (
        <div className={'flex flex-row gap-2'}>
          <Button
            label={t('center.costs.save')}
            variant={'ghost'}
            icon={CheckMarkIcon}
            loading={loading}
            disabled={disabled}
            onClick={handleSave}
          />
          <ActionButton
            variant={'danger'}
            icon={XIcon}
            disabled={disabled}
            onClick={() => {
              onCancel?.()
              setEditable(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

type CenterCostsTableFooterProps = {
  currency: CurrencyEnum
  costType: 'original-cost' | 'final-cost'
  variant: VariantColor
  textColor?: TailwindColor
  label?: string
  total?: number
  initialAmount?: number
  finalAmount?: number
  balance?: number
}

type CenterCostsTableColor = { variant: VariantColor; textColor: TailwindColor }

type CenterCostsTableColorMapping = Record<CenterCostType, CenterCostsTableColor>

export const centerCostsTableColorMapping: Record<RoleEnum, CenterCostsTableColorMapping> = {
  ADMIN: {
    'final-cost': { variant: 'warning', textColor: tailwindColors.secondary.DEFAULT },
    'original-cost': { variant: 'warning', textColor: tailwindColors.secondary.DEFAULT }
  },
  Ol: {
    'final-cost': { variant: 'fushia', textColor: tailwindColors.fushia.DEFAULT },
    'original-cost': { variant: 'purple', textColor: tailwindColors.purple.DEFAULT }
  },
  PCR: {
    'final-cost': { variant: 'fushia', textColor: tailwindColors.fushia.DEFAULT },
    'original-cost': { variant: 'purple', textColor: tailwindColors.purple.DEFAULT }
  },
  STAFF: {
    'final-cost': { variant: 'fushia', textColor: tailwindColors.fushia.DEFAULT },
    'original-cost': { variant: 'purple', textColor: tailwindColors.purple.DEFAULT }
  },
  CLIENT: {
    'final-cost': { variant: 'fushia', textColor: tailwindColors.fushia.DEFAULT },
    'original-cost': { variant: 'purple', textColor: tailwindColors.purple.DEFAULT }
  },
  ACCOUNTANT: {
    'final-cost': { variant: 'fushia', textColor: tailwindColors.fushia.DEFAULT },
    'original-cost': { variant: 'purple', textColor: tailwindColors.purple.DEFAULT }
  },
  SYSTEM_ADMIN: {
    'final-cost': { variant: 'warning', textColor: tailwindColors.secondary.DEFAULT },
    'original-cost': { variant: 'warning', textColor: tailwindColors.secondary.DEFAULT }
  }
}

type LabelBadgeProps = {
  currency: CurrencyEnum
  label: string
  variant: VariantColor
  value?: number
}

function LabelBadge(props: LabelBadgeProps) {
  const { currency, label, variant, value } = props
  return (
    <div className={'flex flex-row gap-2 items-center rounded bg-white-active p-2'}>
      <Typography variant={'secondary-body'}>{label}</Typography>
      <Badge text={NumberFormatter.formatCurrency(value ?? 0, currency)} variant={variant} />
    </div>
  )
}

export function CenterCostsTableFooter(props: CenterCostsTableFooterProps) {
  const { currency, costType, variant, textColor, label } = props
  const { t } = useTranslate()

  if (costType === 'original-cost')
    return (
      <div className={'w-full flex flex-row justify-between items-center pr-6'}>
        {label ? <Typography variant={'tag'}>{label}</Typography> : <div />}
        <Badge
          text={NumberFormatter.formatCurrency(props.total ?? 0, currency)}
          variant={variant}
        />
      </div>
    )

  return (
    <div className={'w-full flex flex-row justify-end items-center gap-2 p-2'}>
      {label && (
        <Typography variant={'tag'} color={textColor}>
          {label}
        </Typography>
      )}

      <LabelBadge
        currency={currency}
        label={t('center.final.costs.footer.initial.amaount')}
        variant={variant}
        value={props.initialAmount}
      />
      <LabelBadge
        currency={currency}
        label={t('center.final.costs.footer.final.amount')}
        variant={variant}
        value={props.finalAmount}
      />

      <div className={'flex flex-row gap-2 items-center rounded bg-white-active p-2'}>
        <Typography variant={'secondary-body'}>
          {t('center.final.costs.footer.balance')}
        </Typography>
        <Badge
          text={`${props.balance && props.balance > 0 ? '+' : ''} ${NumberFormatter.formatCurrency(props.balance ?? 0, currency)}`}
          variant={props.balance && props.balance >= 0 ? 'success' : 'secondaryRed'}
        />
      </div>
    </div>
  )
}

export function CenterTableSkeleton() {
  return <Skeleton className={'w-full h-96'} />
}

export const userUploadInvoiceColorMapping: Record<RoleEnum, ButtonVariant> = {
  Ol: 'outline-fushia',
  ADMIN: 'outline',
  PCR: 'outline-fushia',
  STAFF: 'outline',
  CLIENT: 'outline-fushia',
  ACCOUNTANT: 'outline',
  SYSTEM_ADMIN: 'outline'
}

type UploadInvoicesButtonProps = {
  label?: string
  types: FileTypeEnum[]
  onChange: (files: IFile[]) => void
  multiple?: boolean
  disabled?: boolean
  variant: ButtonVariant
}

export function UploadInvoicesButton(props: UploadInvoicesButtonProps) {
  const { label, types, disabled, multiple, onChange, variant } = props
  const { t } = useTranslate()
  const ref = useRef<HTMLInputElement>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files?.length) return

    const filesList: IFile[] = Array.from(files).map((file) => ({
      name: file.name,
      path: URL.createObjectURL(file),
      size: file.size,
      type: getFileType(file)
    }))

    onChange(filesList)
  }

  return (
    <>
      <input
        className={'hidden'}
        ref={ref}
        accept={getAcceptAttribute(types)}
        type={'file'}
        disabled={props.disabled}
        onChange={handleFileChange}
        multiple={multiple}
      />
      <Button
        label={label ?? t('center.costs.upload.invoice')}
        icon={UploadIcon}
        variant={variant}
        disabled={disabled}
        onClick={() => ref.current?.click()}
      />
    </>
  )
}

type InvoiceColor = 'fushia' | 'purple'

export const userInvoiceColorMapping: Record<RoleEnum, InvoiceColor> = {
  Ol: 'fushia',
  ADMIN: 'purple',
  PCR: 'fushia',
  STAFF: 'fushia',
  CLIENT: 'fushia',
  ACCOUNTANT: 'purple',
  SYSTEM_ADMIN: 'purple'
}

type InvoiceColorMapping = {
  color: TailwindColor
  backgroundColor: string
}

type InvoiceButtonProps = {
  invoice: FileType | IFile
  variant: InvoiceColor
  readOnly?: boolean
  disabled?: boolean
  onDelete?: (id?: number) => void
}

export function InvoiceButton(props: InvoiceButtonProps) {
  const { invoice, readOnly, onDelete, disabled, variant } = props
  const device = useBreakPoint()
  const isMobile = device === 'mobile'
  const { modalState, selectedData, toggleModal } = useModalManager<
    'display-invoice',
    FileType | IFile
  >(['display-invoice'])

  const colorMapping: Record<InvoiceColor, InvoiceColorMapping> = {
    fushia: { color: tailwindColors.fushia.DEFAULT, backgroundColor: 'bg-fushia-light' },
    purple: { color: tailwindColors.secondary.DEFAULT, backgroundColor: 'bg-secondary-light' }
  }

  return (
    <div
      className="w-40 flex flex-row gap-3 border border-white-dark rounded items-center h-9 justify-between p-1 cursor-pointer hover:bg-secondary-light-active"
      onClick={() => {
        if (!invoice?.path) return

        if (isMobile) window.open(invoice.path, '_blank')
        else toggleModal('display-invoice', invoice)
      }}
    >
      <div className={'flex flex-row gap-3 justify-start items-center w-full'}>
        <div
          className={cn(
            'rounded-sm p-1 flex items-center justify-center w-7 h-7',
            colorMapping[variant].backgroundColor
          )}
        >
          <InvoiceIcon color={colorMapping[variant].color} />
        </div>

        <div className={'flex flex-col'}>
          <Typography variant={'body'}>{StringFormatter.shortenText(invoice.name, 8)}</Typography>
          <Typography variant={'small-medium'} color={tailwindColors.grey.light.active}>
            {NumberFormatter.formatBytes(invoice.size)}
          </Typography>
        </div>
      </div>

      {!readOnly && (
        <div className={'w-7 h-7 flex items-center justify-center'}>
          <XIcon
            color={tailwindColors.grey.light.active}
            className={'w-3 h-3 cursor-pointer'}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled && onDelete) {
                if ('id' in invoice) onDelete(invoice.id)
                else onDelete()
              }
            }}
          />
        </div>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState['display-invoice']}
          onCancel={() => toggleModal('display-invoice')}
          hideActions
          hideHeader
          size={'large'}
        >
          <DisplayImage
            allowAll
            type={selectedData.type}
            src={selectedData.path}
            className={cn('w-full rounded-sm', {
              'h-[70vh]': selectedData.type === FileTypeEnum.PDF
            })}
          />
        </Modal>
      )}
    </div>
  )
}

const CostMissingInvoiceKeyMapping: Record<CostMissingInvoiceKeys, LocaleKeys> = {
  'exceptional-expenses': 'center.costs.missing.invoices.exceptional.expenses',
  activities: 'center.costs.missing.invoices.activities',
  transfers: 'center.costs.missing.invoices.transfers',
  'cultural-outings': 'center.costs.missing.invoices.cultural.outings',
  'teaching-hall-costs': 'center.costs.missing.invoices.teaching.hall.costs',
  'tefl-costs': 'center.costs.missing.invoices.tefl.costs'
}

export function CenterMissingInvoices({
  invoices,
  className
}: {
  invoices: CostMissingInvoices[]
  className?: string
}) {
  const { t } = useTranslate()

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {invoices.map((invoice, index) => (
        <div
          key={index}
          className={'flex flex-row gap-2 items-center border border-grey-light rounded p-3'}
        >
          <ExclamationMarkOutlinedIcon
            color={tailwindColors.red.DEFAULT}
            className={'bg-red-light rounded-full w-5 h-5'}
          />

          <Typography variant={'secondary-body'}>
            {t('center.costs.missing.invoices.for', {
              title: invoice.key === 'tefl-costs' ? t(invoice.label as LocaleKeys) : invoice.label,
              key: t(CostMissingInvoiceKeyMapping[invoice.key])
            })}
          </Typography>
        </div>
      ))}
    </div>
  )
}

type UploadInvoiceComponentProps = {
  label?: string
  invoice?: FileType
  readOnly?: boolean
  disabled?: boolean
  variant: InvoiceColor
  onDelete?: (id: number) => void
  onChange?: (file: IFile) => void
}

export function UploadInvoiceComponent(props: UploadInvoiceComponentProps) {
  const { label, invoice, readOnly, disabled, variant, onDelete, onChange } = props

  const buttonVariant: Record<InvoiceColor, ButtonVariant> = {
    fushia: 'outline-fushia',
    purple: 'outline'
  }

  return (
    <div className="flex flex-wrap gap-2 p-2">
      {invoice ? (
        <InvoiceButton
          variant={variant}
          invoice={invoice}
          readOnly={readOnly}
          disabled={disabled}
          onDelete={() => onDelete?.(invoice.id)}
        />
      ) : readOnly ? (
        '-'
      ) : (
        <UploadInvoicesButton
          variant={buttonVariant[variant]}
          label={label}
          disabled={disabled}
          types={[FileTypeEnum.PDF, FileTypeEnum.IMAGE]}
          onChange={(files) => {
            if (files.length > 0) onChange?.(files[0])
          }}
        />
      )}
    </div>
  )
}
